import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import { toast } from 'react-toastify';
import AuthPageWrapper from '../../components/AuthWrapper/AuthPageWrapper';
import { appContext } from '../../service/AppContext';
import './Broadcast.css';

const STATE_LABELS = {
    pending:          'ממתין לתחילת השידור',
    live:             'משדרים עכשיו',
    ended_processing: 'השידור הסתיים, הקלטה בעיבוד',
    published:       'פורסם באתר',
    failed:           'נכשל',
    orphan:           'ללא התאמה',
};

export default function BroadcastLive() {
    const { id } = useParams();
    const { breslevRequest } = useContext(appContext);
    const navigate = useNavigate();
    const [bcast, setBcast] = useState(null);
    const videoRef = useRef(null);
    const hlsRef = useRef(null);
    const cancelling = useRef(false);

    useEffect(() => {
        let stopped = false;
        const fetchOnce = async () => {
            const res = await breslevRequest({ method: 'GET', route: `/broadcast/${id}` });
            if (!stopped && res?.status === 200) setBcast(res.data);
        };
        fetchOnce();
        const interval = setInterval(fetchOnce, 5000);
        return () => { stopped = true; clearInterval(interval); };
    }, [id, breslevRequest]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !bcast?.live_hls_url) return;
        if (bcast.state !== 'live') return;

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = bcast.live_hls_url;
            return;
        }
        if (Hls.isSupported()) {
            if (hlsRef.current) {
                try { hlsRef.current.destroy(); } catch (_) { /* noop */ }
            }
            const hls = new Hls();
            hls.loadSource(bcast.live_hls_url);
            hls.attachMedia(video);
            hlsRef.current = hls;
        }
        return () => {
            if (hlsRef.current) {
                try { hlsRef.current.destroy(); } catch (_) { /* noop */ }
                hlsRef.current = null;
            }
        };
    }, [bcast?.state, bcast?.live_hls_url]);

    const onCancel = async () => {
        if (!window.confirm('לבטל את השידור?')) return;
        cancelling.current = true;
        const res = await breslevRequest({ method: 'POST', route: `/broadcast/${id}/cancel` });
        cancelling.current = false;
        if (res?.status === 200) {
            toast('השידור בוטל');
            setBcast(res.data);
        } else {
            toast('שגיאה בביטול');
        }
    };

    const onRetry = async () => {
        const res = await breslevRequest({ method: 'POST', route: `/broadcast/${id}/retry-publish` });
        if (res?.status === 200) {
            toast('פרסום מנסה שוב');
            setBcast(res.data);
        } else {
            toast('שגיאה בפרסום מחדש');
        }
    };

    const onSetWpStatus = async (status) => {
        const res = await breslevRequest({
            method: 'POST',
            route: `/broadcast/${id}/wp-status`,
            body: { status },
        });
        if (res?.status === 200) {
            toast(status === 'publish' ? 'הפוסט פורסם' : 'הפוסט הועבר לטיוטה');
            setBcast(res.data);
        } else {
            toast('שגיאה בעדכון סטטוס הפוסט');
        }
    };

    const onDelete = async () => {
        if (!window.confirm('למחוק את השידור? הפעולה תסיר גם את הפוסט באתר.')) return;
        const res = await breslevRequest({ method: 'DELETE', route: `/broadcast/${id}` });
        if (res?.status === 204 || res?.status === 200) {
            toast('השידור נמחק');
            navigate('/broadcast');
        } else {
            toast('שגיאה במחיקת השידור');
        }
    };

    return (
        <AuthPageWrapper roles={['ContentManager', 'Admin']}>
            <div className='video-page-wrepper'>
                <div className='video-page'>
                    <div className='page-header'>שידור חי</div>
                    <hr />
                    {!bcast && <div>טוען...</div>}
                    {bcast && (
                        <div className='page-container broadcast-live'>
                            <div className='bl-row'>
                                <div className='bl-label'>שפה</div>
                                <div>{bcast.language_code}</div>
                            </div>
                            <div className='bl-row'>
                                <div className='bl-label'>מפתח שידור</div>
                                <div dir='ltr'>{bcast.stream_key}</div>
                            </div>
                            <div className='bl-row'>
                                <div className='bl-label'>כותרת</div>
                                <div>{bcast.title}</div>
                            </div>
                            <div className='bl-row'>
                                <div className='bl-label'>סטטוס</div>
                                <div className={'bl-state bl-state-' + bcast.state}>{STATE_LABELS[bcast.state] || bcast.state}</div>
                            </div>
                            {bcast.error_msg && (
                                <div className='bl-row'>
                                    <div className='bl-label'>שגיאה</div>
                                    <div className='bl-error'>{bcast.error_msg}</div>
                                </div>
                            )}
                            {bcast.state === 'live' && bcast.live_hls_url && (
                                <video ref={videoRef} className='bl-player' controls playsInline autoPlay muted />
                            )}
                            {bcast.state === 'published' && bcast.wp_post_url && (
                                <a className='bl-cta' href={bcast.wp_post_url} target='_blank' rel='noreferrer'>
                                    צפו בהקלטה המלאה באתר
                                </a>
                            )}
                            {bcast.wp_post_id && (
                                <div className='bl-row'>
                                    <div className='bl-label'>סטטוס פוסט באתר</div>
                                    <div className='bl-wp-status'>
                                        <span>{bcast.wp_post_status === 'draft' ? 'טיוטה' : 'פורסם'}</span>
                                        {bcast.wp_post_status === 'draft'
                                            ? <button onClick={() => onSetWpStatus('publish')} className='upload-button bl-secondary'>פרסם</button>
                                            : <button onClick={() => onSetWpStatus('draft')} className='upload-button bl-secondary'>העבר לטיוטה</button>}
                                    </div>
                                </div>
                            )}
                            <div className='bl-actions'>
                                {['pending', 'live'].includes(bcast.state) && (
                                    <button onClick={onCancel} className='upload-button bl-cancel'>בטל שידור</button>
                                )}
                                {bcast.state === 'failed' && bcast.video_id && (
                                    <button onClick={onRetry} className='upload-button'>נסה לפרסם שוב</button>
                                )}
                                <button onClick={onDelete} className='upload-button bl-cancel'>מחק שידור</button>
                                <button onClick={() => navigate('/broadcast')} className='upload-button bl-secondary'>חזרה לרשימה</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AuthPageWrapper>
    );
}
